<template>
  <div class="rolling-exp">
    <h1 class="title">🎱 Катящийся цилиндр на неровной дорожке</h1>
    <p class="subtitle">
      Чистое качение, скольжение, препятствия и подпрыгивания
    </p>

    <div class="layout">
      <!-- Левая колонка: canvas'ы -->
      <div class="canvas-column">
        <div class="canvas-wrap">
          <canvas ref="simCanvas" :width="W" :height="H" />
        </div>
        <div class="graph-wrap">
          <canvas ref="graphCanvas" :width="W" :height="GH" />
        </div>
      </div>

      <!-- Правая панель -->
      <div class="side-panel">
        <div class="panel-block">
          <h3>Тело</h3>
          <div class="shape-select">
            <button
              v-for="sh in shapes"
              :key="sh.key"
              :class="['shape-btn', { active: shape === sh.key }]"
              @click="shape = sh.key; reset()"
            >
              <span class="shape-icon">{{ sh.icon }}</span>
              <span>{{ sh.name }}</span>
              <span class="beta">β = {{ sh.beta }}</span>
            </button>
          </div>
        </div>

        <div class="panel-block">
          <h3>Параметры</h3>
          <div class="control-group">
            <label>Угол наклона α</label>
            <input v-model.number="angleDeg" type="range" min="5" max="60" step="1" class="slider" />
            <span class="value">{{ angleDeg }}°</span>
          </div>
          <div class="control-group">
            <label>Коэфф. трения μ</label>
            <input v-model.number="mu" type="range" min="0" max="1.0" step="0.05" class="slider" />
            <span class="value">{{ mu.toFixed(2) }}</span>
          </div>
          <div class="control-group">
            <label>Масса m</label>
            <input v-model.number="mass" type="range" min="0.5" max="5" step="0.5" class="slider" />
            <span class="value">{{ mass }} кг</span>
          </div>
          <div class="control-group">
            <label>Радиус R</label>
            <input v-model.number="radius" type="range" min="20" max="60" step="5" class="slider" />
            <span class="value">{{ radius }} см</span>
          </div>
        </div>

        <div class="panel-block">
          <h3>Препятствия</h3>
          <div class="btn-row">
            <button class="btn-add bump" @click="addBump">+ Добавить кочку</button>
            <button class="btn-reset" @click="clearBumps">Очистить</button>
          </div>
          <div class="bump-list">
            <div v-for="(b, i) in bumps" :key="i" class="bump-item">
              <span>Кочка {{ i + 1 }}: s={{ Math.round(b.s) }}, r={{ b.r }}</span>
              <button class="btn-del" @click="removeBump(i)">×</button>
            </div>
            <div v-if="bumps.length === 0" class="bump-empty">Нет препятствий</div>
          </div>
        </div>

        <div class="panel-block">
          <h3>Управление</h3>
          <div class="btn-row">
            <button class="btn-run" @click="toggleRun">{{ running ? '⏸ Стоп' : '▶ Пуск' }}</button>
            <button class="btn-reset" @click="reset">↺ Сброс</button>
          </div>
          <label class="check-row">
            <input v-model="showForces" type="checkbox" />
            <span>Векторы сил</span>
          </label>
        </div>

        <div class="panel-block">
          <h3>Текущие значения</h3>
          <div class="energy-row">
            <span class="e-label">v</span>
            <span class="e-value">{{ (v / PIXEL_PER_M).toFixed(2) }} м/с</span>
          </div>
          <div class="energy-row">
            <span class="e-label">ω</span>
            <span class="e-value">{{ omega.toFixed(1) }} рад/с</span>
          </div>
          <div class="energy-row">
            <span class="e-label">s</span>
            <span class="e-value">{{ (s / PIXEL_PER_M).toFixed(2) }} м</span>
          </div>
          <div class="energy-row">
            <span class="e-label">t</span>
            <span class="e-value">{{ t.toFixed(2) }} с</span>
          </div>
          <div class="energy-row total">
            <span class="e-label">Режим</span>
            <span class="e-value" :style="{ color: sliding ? '#fbbf24' : '#4ade80' }">
              {{ sliding ? 'Скольжение' : 'Качение' }}
            </span>
          </div>
        </div>

        <div v-if="showEnergies" class="panel-block">
          <h3>Энергии, Дж</h3>
          <div class="energy-row">
            <span class="e-label" style="color:#f87171">Eпот</span>
            <span class="e-value">{{ energies.Epot.toFixed(2) }}</span>
          </div>
          <div class="energy-row">
            <span class="e-label" style="color:#fbbf24">Eкин пост</span>
            <span class="e-value">{{ energies.EkinT.toFixed(2) }}</span>
          </div>
          <div class="energy-row">
            <span class="e-label" style="color:#4ade80">Eкин вр</span>
            <span class="e-value">{{ energies.EkinR.toFixed(2) }}</span>
          </div>
          <div class="energy-row">
            <span class="e-label" style="color:#a78bfa">Qтеп</span>
            <span class="e-value">{{ energies.Eheat.toFixed(2) }}</span>
          </div>
          <div class="energy-row total">
            <span class="e-label">ΣE</span>
            <span class="e-value">{{ energies.total.toFixed(2) }}</span>
          </div>
        </div>

        <div class="panel-block info">
          <h3>Справка</h3>
          <p>β = I / (mR²)</p>
          <p>a<sub>кач</sub> = g·sinα / (1+β)</p>
          <p>Условие качения: μ ≥ tanα · β/(1+β)</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'

/* ==================== КОНФИГУРАЦИЯ ==================== */
const W = 900
const H = 520
const GH = 160
const g = 9.81
const PLANE_LEN = 720
const DT = 0.016
const PIXEL_PER_M = 200

/* ==================== ПАРАМЕТРЫ ==================== */
const shapes = [
  { key: 'cylinder', name: 'Цилиндр', icon: '🛢️', beta: 0.50 },
  { key: 'sphere',   name: 'Шар',     icon: '⚽', beta: 0.40 },
  { key: 'hoop',     name: 'Кольцо',  icon: '🥏', beta: 1.00 },
]
const shape = ref('cylinder')
const angleDeg = ref(25)
const mu = ref(0.25)
const mass = ref(2)
const radius = ref(35)

/* ==================== СОСТОЯНИЕ ==================== */
const simCanvas = ref(null)
const graphCanvas = ref(null)
let simCtx = null
let graphCtx = null

const running = ref(false)
const showForces = ref(true)
const showEnergies = ref(true)

const s = ref(0)
const v = ref(0)
const omega = ref(0)
const phi = ref(0)
const t = ref(0)
const sliding = ref(false)
const history = ref([])

const bumps = ref([
  { s: 220, r: 30 },
  { s: 480, r: 40 },
])

let animId = null
let jumpOffset = 0
let jumpVy = 0

/* ==================== ВЫЧИСЛЯЕМЫЕ ==================== */
const alpha = computed(() => angleDeg.value * Math.PI / 180)
const beta = computed(() => shapes.find(sh => sh.key === shape.value).beta)
const planeStart = { x: 80, y: 80 }


const muMin = computed(() => Math.tan(alpha.value) * beta.value / (1 + beta.value))
const willSlideInitially = computed(() => mu.value < muMin.value)

const energies = computed(() => {
  const m = mass.value
  const vm = v.value / PIXEL_PER_M
  const Rm = radius.value / PIXEL_PER_M
  const h = getSurfaceHeight(s.value) / PIXEL_PER_M
  const H0 = (PLANE_LEN / PIXEL_PER_M) * Math.sin(alpha.value)
  const Epot = m * g * Math.max(0, H0 - h)
  const EkinT = 0.5 * m * vm * vm
  const EkinR = 0.5 * m * beta.value * Rm * Rm * omega.value * omega.value
  const Eheat = Math.max(0, m * g * H0 - Epot - EkinT - EkinR)
  return { Epot, EkinT, EkinR, Eheat, total: Epot + EkinT + EkinR + Eheat }
})

/* ==================== BUMPS ==================== */
function getBumpHeight(sPos) {
  let h = 0
  for (const b of bumps.value) {
    const ds = sPos - b.s
    if (Math.abs(ds) < b.r) {
      h = Math.max(h, Math.sqrt(b.r * b.r - ds * ds))
    }
  }
  return h
}

function getBumpSlope(sPos) {
  let slope = 0
  for (const b of bumps.value) {
    const ds = sPos - b.s
    if (Math.abs(ds) < b.r && b.r > 0) {
      const h = Math.sqrt(b.r * b.r - ds * ds)
      if (h > 0.1) {
        slope += -ds / h
      }
    }
  }
  return Math.max(-2, Math.min(2, slope))
}

function getSurfaceHeight(sPos) {
  return sPos * Math.sin(alpha.value) + getBumpHeight(sPos)
}

/* ==================== ИНИЦИАЛИЗАЦИЯ ==================== */
onMounted(() => {
  simCtx = simCanvas.value.getContext('2d')
  graphCtx = graphCanvas.value.getContext('2d')
  reset()
})
onUnmounted(() => { if (animId) cancelAnimationFrame(animId) })

/* ==================== УПРАВЛЕНИЕ ==================== */
function addBump() {
  const sPos = 100 + Math.random() * (PLANE_LEN - 200)
  const r = 20 + Math.floor(Math.random() * 25)
  bumps.value.push({ s: sPos, r })
  drawSim()
}
function clearBumps() {
  bumps.value = []
  drawSim()
}
function removeBump(i) {
  bumps.value.splice(i, 1)
  drawSim()
}

function toggleRun() {
  running.value = !running.value
  if (running.value) loop()
}

function reset() {
  running.value = false
  if (animId) cancelAnimationFrame(animId)
  s.value = 0
  v.value = 0
  omega.value = 0
  phi.value = 0
  t.value = 0
  jumpOffset = 0
  jumpVy = 0
  sliding.value = willSlideInitially.value
  history.value = []
  drawSim()
  drawGraph()
}

function loop() {
  if (!running.value) return
  update()
  drawSim()
  drawGraph()
  animId = requestAnimationFrame(loop)
}

/* ==================== ФИЗИКА ==================== */
function update() {
  const a = alpha.value
  const b = beta.value
  const Rpx = radius.value
  const Rm = Rpx / PIXEL_PER_M
  const vm = v.value / PIXEL_PER_M
  const slope = getBumpSlope(s.value)
  const effAlpha = a + Math.atan(slope)

  // Подпрыгивание: если на спуске с кочки на высокой скорости
  const wasBeforePeak = getBumpSlope(s.value - v.value * DT * 2) > 0.2
  const isAfterPeak = slope < -0.2
  if (wasBeforePeak && isAfterPeak && Math.abs(vm) > 1.5 && jumpOffset <= 0) {
    jumpVy = -Math.min(vm * 8, 40)
    jumpOffset = Math.abs(jumpVy)
  }

  if (jumpOffset > 0) {
    jumpVy += 80 * DT // "гравитация" подпрыжка, px/с²
    jumpOffset += jumpVy * DT
    if (jumpOffset >= 0) {
      jumpOffset = 0
      jumpVy = 0
    }
  }

  if (sliding.value) {
    const aSlip = g * (Math.sin(effAlpha) - mu.value * Math.cos(effAlpha))
    const eps = (mu.value * g * Math.cos(effAlpha)) / (b * Rm)
    const newVm = vm + aSlip * DT
    const newOmega = omega.value + eps * DT
    const newVRot = newOmega * Rpx / PIXEL_PER_M
    if (newVm <= newVRot + 0.01) {
      sliding.value = false
      v.value = newVRot * PIXEL_PER_M
      omega.value = newVm / Rm
    } else {
      v.value = newVm * PIXEL_PER_M
      omega.value = newOmega
    }
  } else {
    const aRoll = (g * Math.sin(effAlpha)) / (1 + b)
    const newVm = vm + aRoll * DT
    v.value = newVm * PIXEL_PER_M
    omega.value = newVm / Rm
  }

  const ds = v.value * DT
  s.value += ds
  phi.value += omega.value * DT
  t.value += DT

  if (Math.floor(t.value * 60) % 3 === 0) {
    history.value.push({ t: t.value, v: v.value / PIXEL_PER_M })
    if (history.value.length > 300) history.value.shift()
  }

  if (s.value >= PLANE_LEN - radius.value) {
    s.value = PLANE_LEN - radius.value
    running.value = false
  }
  if (s.value < 0) {
    s.value = 0
    v.value = 0
    running.value = false
  }
}

/* ==================== КООРДИНАТЫ ==================== */
function getBodyCenter() {
  const a = alpha.value
  const bh = getBumpHeight(s.value)
  const n = radius.value + bh + jumpOffset
  const cx = planeStart.x + s.value * Math.cos(a)
  const cy = planeStart.y + s.value * Math.sin(a) - n
  return { cx, cy }
}

/* ==================== ОТРИСОВКА SIM ==================== */
function drawSim() {
  if (!simCtx) return
  simCtx.clearRect(0, 0, W, H)
  simCtx.fillStyle = '#0b0f19'
  simCtx.fillRect(0, 0, W, H)

  drawSky()
  drawPlane()
  drawBody()
  if (showForces.value) drawForces()
}

function drawSky() {
  // Далёкие горы для красоты
  simCtx.fillStyle = '#161b22'
  simCtx.beginPath()
  simCtx.moveTo(0, H)
  simCtx.lineTo(0, H - 150)
  simCtx.lineTo(150, H - 220)
  simCtx.lineTo(350, H - 180)
  simCtx.lineTo(600, H - 240)
  simCtx.lineTo(W, H - 160)
  simCtx.lineTo(W, H)
  simCtx.closePath()
  simCtx.fill()
}

function drawPlane() {
  const a = alpha.value
  const sx = planeStart.x
  const sy = planeStart.y

  // Точки поверхности
  const pts = []
  for (let ds = 0; ds <= PLANE_LEN; ds += 4) {
    const bh = getBumpHeight(ds)
    const px = sx + ds * Math.cos(a)
    const py = sy + ds * Math.sin(a) - bh
    pts.push({ x: px, y: py })
  }

  // Заливка под дорожкой
  simCtx.fillStyle = '#1a2332'
  simCtx.beginPath()
  simCtx.moveTo(pts[0].x, pts[0].y)
  for (let i = 1; i < pts.length; i++) simCtx.lineTo(pts[i].x, pts[i].y)
  simCtx.lineTo(pts[pts.length - 1].x, H)
  simCtx.lineTo(pts[0].x, H)
  simCtx.closePath()
  simCtx.fill()

  // Линия поверхности
  simCtx.strokeStyle = '#58a6ff'
  simCtx.lineWidth = 3
  simCtx.lineJoin = 'round'
  simCtx.beginPath()
  simCtx.moveTo(pts[0].x, pts[0].y)
  for (let i = 1; i < pts.length; i++) simCtx.lineTo(pts[i].x, pts[i].y)
  simCtx.stroke()

  // Штриховка основания
  simCtx.strokeStyle = '#21262d'
  simCtx.lineWidth = 1
  for (let i = 0; i <= PLANE_LEN; i += 40) {
    const bh = getBumpHeight(i)
    const px = sx + i * Math.cos(a)
    const py = sy + i * Math.sin(a) - bh
    simCtx.beginPath()
    simCtx.moveTo(px, py)
    simCtx.lineTo(px, py + 20)
    simCtx.stroke()
  }

  // Дуга угла
  const arcR = 45
  simCtx.strokeStyle = '#8b949e'
  simCtx.lineWidth = 1.5
  simCtx.beginPath()
  simCtx.arc(sx, sy, arcR, 0, a)
  simCtx.stroke()
  simCtx.fillStyle = '#8b949e'
  simCtx.font = '13px sans-serif'
  simCtx.fillText(`α = ${angleDeg.value}°`, sx + arcR + 6, sy + 18)
}

function drawBody() {
  const { cx, cy } = getBodyCenter()
  const r = radius.value
  const bh = getBumpHeight(s.value)

  // Тень (смещена вниз-вправо)
  simCtx.fillStyle = 'rgba(0,0,0,0.25)'
  simCtx.beginPath()
  const shadowOff = 6 + bh * 0.3
  simCtx.ellipse(cx + shadowOff, cy + shadowOff + r * 0.2, r * 0.9, r * 0.2, 0, 0, Math.PI * 2)
  simCtx.fill()

  // Тело
  const grad = simCtx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r)
  grad.addColorStop(0, '#66bb6a')
  grad.addColorStop(1, '#2e7d32')
  simCtx.fillStyle = grad
  simCtx.beginPath()
  simCtx.arc(cx, cy, r, 0, Math.PI * 2)
  simCtx.fill()
  simCtx.strokeStyle = '#1b5e20'
  simCtx.lineWidth = 2
  simCtx.stroke()

  // Линия радиуса (вращение)
  simCtx.strokeStyle = '#fff'
  simCtx.lineWidth = 2.5
  simCtx.beginPath()
  simCtx.moveTo(cx, cy)
  simCtx.lineTo(cx + r * Math.cos(phi.value), cy + r * Math.sin(phi.value))
  simCtx.stroke()

  // Центр
  simCtx.fillStyle = '#fff'
  simCtx.beginPath()
  simCtx.arc(cx, cy, 3, 0, Math.PI * 2)
  simCtx.fill()
}

function drawForces() {
  const { cx, cy } = getBodyCenter()
  const a = alpha.value
  const scale = 10
  const mg = mass.value * g * scale
  const N = mg * Math.cos(a)
  const Fr = mg * Math.sin(a)
  const Ffr = mu.value * mg * Math.cos(a)

  // mg — вниз
  drawArrow(cx, cy, cx, cy + mg, '#f87171', 'mg')
  // N — вертикально вверх
  drawArrow(cx, cy, cx, cy - N, '#4ade80', 'N')
  // mg·sinα — вдоль плоскости вниз
  drawArrow(cx, cy, cx + Fr * Math.cos(a), cy + Fr * Math.sin(a), '#fbbf24', 'mg·sinα')
  // Fтр — вдоль плоскости вверх
  if (mu.value > 0) {
    drawArrow(cx, cy, cx - Ffr * Math.cos(a), cy - Ffr * Math.sin(a), '#60a5fa', 'Fтр')
  }
}

function drawArrow(x1, y1, x2, y2, color, label) {
  const head = 6
  const angle = Math.atan2(y2 - y1, x2 - x1)
  simCtx.strokeStyle = color
  simCtx.fillStyle = color
  simCtx.lineWidth = 2
  simCtx.beginPath()
  simCtx.moveTo(x1, y1)
  simCtx.lineTo(x2, y2)
  simCtx.stroke()
  simCtx.beginPath()
  simCtx.moveTo(x2, y2)
  simCtx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6))
  simCtx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6))
  simCtx.closePath()
  simCtx.fill()
  if (label) {
    simCtx.font = '12px sans-serif'
    simCtx.fillText(label, x2 + 8, y2 + 4)
  }
}

/* ==================== ОТРИСОВКА ГРАФИКА ==================== */
function drawGraph() {
  if (!graphCtx) return
  const ctx = graphCtx
  ctx.clearRect(0, 0, W, GH)
  ctx.fillStyle = '#0d1117'
  ctx.fillRect(0, 0, W, GH)

  if (history.value.length < 2) {
    ctx.fillStyle = '#8b949e'
    ctx.font = '14px sans-serif'
    ctx.fillText('График скорости v(t)', 20, GH / 2)
    return
  }

  const maxV = Math.max(2, ...history.value.map(p => p.v)) * 1.15
  const maxT = Math.max(1, history.value[history.value.length - 1].t)

  const padL = 50, padR = 20, padB = 30, padT = 20
  const gw = W - padL - padR
  const gh = GH - padB - padT

  const px = (t) => padL + (t / maxT) * gw
  const py = (v) => padT + gh - (v / maxV) * gh

  // Сетка по Y
  ctx.strokeStyle = '#21262d'
  ctx.lineWidth = 0.5
  for (let i = 0; i <= 4; i++) {
    const y = py(maxV * i / 4)
    ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke()
    ctx.fillStyle = '#8b949e'
    ctx.font = '11px sans-serif'
    ctx.fillText((maxV * i / 4).toFixed(1), 8, y + 4)
  }

  // Сетка по X
  for (let i = 0; i <= 5; i++) {
    const x = px(maxT * i / 5)
    ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, GH - padB); ctx.stroke()
    ctx.fillStyle = '#8b949e'
    ctx.fillText((maxT * i / 5).toFixed(1), x - 8, GH - 10)
  }

  // Оси
  ctx.strokeStyle = '#8b949e'
  ctx.lineWidth = 1
  ctx.beginPath(); ctx.moveTo(padL, py(0)); ctx.lineTo(W - padR, py(0)); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, GH - padB); ctx.stroke()

  // Кривая
  ctx.strokeStyle = '#58a6ff'
  ctx.lineWidth = 2.5
  ctx.beginPath()
  ctx.moveTo(px(history.value[0].t), py(history.value[0].v))
  for (let i = 1; i < history.value.length; i++) {
    ctx.lineTo(px(history.value[i].t), py(history.value[i].v))
  }
  ctx.stroke()

  // Заголовок
  ctx.fillStyle = '#c9d1d9'
  ctx.font = 'bold 13px sans-serif'
  ctx.fillText('Скорость v(t), м/с', padL, 16)
}

watch([angleDeg, mu, mass, radius], () => {
  if (!running.value) {
    sliding.value = willSlideInitially.value
    drawSim()
  }
})
</script>

<style scoped>
.rolling-exp {
  max-width: 1300px;
  margin: 0 auto;
  padding: 24px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #c9d1d9;
}

.title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #58a6ff;
}

.subtitle {
  font-size: 13px;
  color: #8b949e;
  margin: 0 0 18px;
}

.layout {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
}

@media (max-width: 1000px) {
  .layout { grid-template-columns: 1fr; }
}

.canvas-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.canvas-wrap, .graph-wrap {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #30363d;
  background: #0b0f19;
}

canvas {
  display: block;
  width: 100%;
  height: auto;
}

.side-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.panel-block {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 10px;
  padding: 14px;
}

.panel-block h3 {
  margin: 0 0 12px;
  font-size: 13px;
  color: #f0f6fc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #30363d;
  padding-bottom: 8px;
}

.shape-select {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shape-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #30363d;
  border-radius: 8px;
  background: #0d1117;
  color: #c9d1d9;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
}

.shape-btn.active {
  border-color: #58a6ff;
  background: rgba(88,166,255,0.1);
}

.shape-icon { font-size: 20px; }
.beta { margin-left: auto; font-size: 11px; color: #8b949e; font-variant-numeric: tabular-nums; }

.control-group { margin-bottom: 12px; }
.control-group label { display: block; font-size: 11px; color: #8b949e; margin-bottom: 4px; }
.slider { width: 100%; margin-bottom: 2px; }
.value { font-size: 12px; color: #c9d1d9; font-variant-numeric: tabular-nums; }

.btn-row { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }

.btn-add.bump { background: #7c3aed; color: #fff; }
.btn-add.bump:hover { background: #8b5cf6; }

.btn-run, .btn-reset {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-run { background: #238636; color: #fff; }
.btn-run:hover { background: #2ea043; }
.btn-reset { background: #21262d; color: #c9d1d9; border: 1px solid #30363d; }
.btn-reset:hover { background: #30363d; }

.bump-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 120px;
  overflow-y: auto;
}
.bump-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  background: rgba(88,166,255,0.05);
  border: 1px solid rgba(88,166,255,0.2);
}
.bump-empty { font-size: 12px; color: #555; text-align: center; padding: 8px; }

.btn-del {
  background: transparent;
  border: none;
  color: #f85149;
  font-size: 16px;
  cursor: pointer;
  line-height: 1;
}

.check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 13px;
  cursor: pointer;
}
.check-row input { accent-color: #58a6ff; }

.energy-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #21262d;
  font-size: 13px;
}
.energy-row.total { border-bottom: none; font-weight: 700; color: #f0f6fc; margin-top: 4px; }
.e-label { font-weight: 600; }
.e-value { font-variant-numeric: tabular-nums; color: #c9d1d9; }

.panel-block.info p {
  font-size: 12px;
  color: #8b949e;
  margin: 5px 0;
  line-height: 1.5;
}
.panel-block.info p sub { font-size: 9px; }
</style>
